import type {MouseEvent, ReactElement} from 'react';
import type {VoiceProfileItemProps} from './voice-profile-item.types';
import {Text, TextSize, TextType} from '@Components/text';
import {getRandomThumbnailImageForAudioItem} from '@Libraries/poster-audio-item.library';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {ClickableDiv} from '@Components/clickable-div';
import styles from './voice-profile-item.module.scss';
import {useAudioPlayback} from '@Hooks/ai-text-to-speech/useAudioPlayback';
import {PlayPauseButton} from '@Panels/ai-voice-panel/components/play-pause-button/play-pause-button';

export function VoiceProfileItem({profile, isSelected, ...props}: VoiceProfileItemProps): ReactElement {
  const audioPlayback = useAudioPlayback();
  const avatarUrl = profile.avatarUrl ?? getRandomThumbnailImageForAudioItem();
  const profileNameColorClass = isSelected ? 'ct-brand-primary' : 'ct-neutral-primary';

  const handlePlayPauseClick = (e: MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    audioPlayback.togglePlayPause(profile.soundtrack);
  };

  const getAccent = (): ReactElement | null => {
    if (!profile.accent) {
      return null;
    }
    return (
      <>
        <span className={styles.separatingDot} aria-hidden={true}>
          ■
        </span>
        <Text size={TextSize.SM} className={styles.secondaryText} val={profile.accent} />
      </>
    );
  };

  const getAvatar = (): ReactElement => {
    return (
      <div className={`flex-center _position-relative radius-8 ${styles.avatarContainer}`}>
        <img className={`_position-absolute ${styles.avatar}`} src={avatarUrl} alt={profile.name} loading="lazy" />
      </div>
    );
  };

  const getNameAndDescription = (): ReactElement => {
    return (
      <div className={'flexbox flex-1 flex-column'}>
        <div className={'flexbox flex-items-center gap-1'}>
          <Text className={`${styles.profileName} ${profileNameColorClass}`} bold size={TextSize.XSMALL} type={TextType.BODY} val={profile.name} />
          {getAccent()}
        </div>
        <Text size={TextSize.SM} className={styles.secondaryText} val={profile.description} />
      </div>
    );
  };

  const getSamplePlaybackButton = (): ReactElement => {
    return (
      <PlayPauseButton
        isPlaying={audioPlayback.isPlaying}
        playbackProgress={audioPlayback.playbackProgress}
        onClick={handlePlayPauseClick}
        baseColor={PMW_COLORS_NEUTRAL.NEUTRAL_6}
        className={styles.playButton}
      />
    );
  };

  return (
    <ClickableDiv
      className={`flex-center radius-8 gap-2 spacing-p-t-1 spacing-p-b-1 spacing-p-l-1 spacing-p-r-2 _cursor-pointer hover-transition-all ${styles.profileItem} ${isSelected ? styles.profileItemSelected : ''}`}
      onClick={(): void => {
        props.onProfileSelected(profile.id);
      }}
    >
      {getAvatar()}
      {getNameAndDescription()}
      {getSamplePlaybackButton()}
    </ClickableDiv>
  );
}
